'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import styles from './image.module.sass';
// import { Spinner } from '../Spinner';
import { Skeleton } from '../Skeleton';

interface ImageContainerProps extends React.HTMLAttributes<HTMLDivElement> {
    src: string;
    ratio?: number; // Optional ratio prop to control scaling
    alignment?: 'start' | 'center' | 'end';
    alt?: string;
    priority?: boolean;
}

export const ImageContainer = ({
    src,
    ratio = 1,
    alignment = 'center',
    alt,
    className,
    priority = false,
    ...props
}: ImageContainerProps) => {
    const imgRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const rafRef = useRef<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const adjustImageSize = useCallback(() => {
        if (rafRef.current != null) return;
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;

            const img = imgRef.current;
            const itemBox = containerRef.current;
            const parentBox = itemBox?.parentElement;

            if (!img || !itemBox || !parentBox) return;

            const itemWidth = parentBox.offsetWidth;
            const itemHeight = parentBox.offsetHeight;

            const realWidth = img.naturalWidth;
            const realHeight = img.naturalHeight;

            if (!itemWidth || !itemHeight || !realWidth || !realHeight) return;

            const BS = Math.pow(itemHeight / ratio, 2);
            const AR = realWidth / realHeight;

            const verticalPadding = Math.max(0, (((itemHeight - Math.sqrt(BS / AR)) / itemHeight) * 100) / 2);
            const horizontalPadding = Math.max(0, (((itemWidth - Math.sqrt(BS * AR)) / itemWidth) * 100) / 2);

            const block = `${verticalPadding}%`;
            const offset = `${horizontalPadding * 2}%`;

            switch (alignment) {
                case 'start':
                    itemBox.style.paddingBlock = block;
                    itemBox.style.paddingInlineStart = '0';
                    itemBox.style.paddingInlineEnd = offset;
                    break;
                case 'end':
                    itemBox.style.paddingBlock = block;
                    itemBox.style.paddingInlineStart = offset;
                    itemBox.style.paddingInlineEnd = '0';
                    break;
                default: {
                    itemBox.style.paddingBlock = block;
                    itemBox.style.paddingInlineStart = `${horizontalPadding}%`;
                    itemBox.style.paddingInlineEnd = `${horizontalPadding}%`;
                    break;
                }
            }
        });
    }, [ratio, alignment]);

    const handleLoaded = useCallback(() => {
        setIsLoading(false);
        adjustImageSize();
    }, [adjustImageSize]);

    useEffect(() => {
        const img = imgRef.current;
        const itemBox = containerRef.current;
        const parentBox = itemBox?.parentElement;

        if (!img || !itemBox || !parentBox) return;

        // cached image: `load` may fire before mount
        if (img.complete && img.naturalWidth) {
            handleLoaded();
        }

        const ro = new ResizeObserver(adjustImageSize);
        ro.observe(parentBox);

        return () => {
            ro.disconnect();
            if (rafRef.current != null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [src, adjustImageSize, handleLoaded]);

    return (
        <div ref={containerRef} className={cn(styles.inner, !isLoading && styles.loaded, className)} {...props}>
            {isLoading && <Skeleton absolute />}
            <img
                ref={imgRef}
                src={src}
                alt={alt ? alt : ''}
                loading={priority ? 'eager' : 'lazy'}
                fetchPriority={priority ? 'high' : 'auto'}
                decoding="async"
                onLoad={handleLoaded}
                onError={handleLoaded}
            />
        </div>
    );
};
